// Coverage Types Enum
export enum CoverageType {
  MTPL = "mtpl",
  ALLRISK = "allRisk",
  ASSISTANCE = "assistance", // This will be the roadside assistance
  ACCIDENT = "accident",
  WINDSHIELD = "windshield",
  GAP = "gap",
  CARGO = "cargo",
  LEGALPROTECTION = "legalProtection",
  DAMAGES = "damages",
}

// Coverage Configuration State Interface
export interface CoverageConfig {
  mtpl: {
    limit: number; // Insurance Payout Limit (LIMITY)
    deductible: number; // Deductible (SPOL_MTPL)
    commercialDiscount: number; // Commercial Discount for MTPL (SLE170)
    voucherCode: string; // Discount Voucher Code (VOUCHER)
  };
  allRisk: {
    sumInsured: number;
    deductible: number; // Deductible amount in CZK
    deductiblePercent: number;
    deductibleMin: number;
    deductibleFixed: number;
    theft: boolean; // Theft coverage
    vandalism: boolean; // Fire coverage
    naturalDisasters: boolean; // Natural hazard coverage
    glass: boolean; // Glass coverage
    territorialScope: string; // Territorial scope of coverage (e.g., "CZ", "EU", "WORLD")
    notes: string; // Additional notes for the coverage
    activeSecurity: string;
    glassIncluded: string;
    machineWorkActivity: boolean;
    commercialDiscount: number;
    voucherCode: string;
    nonStandardEquipment: {
      specification: string;
      insuredAmount: number;
    }[];
    machineryInsurance: boolean;
    machineryInsuranceLimit: number;
    machineryInsuredObject: string; // 'C' for whole vehicle, 'N' for vehicle superstructure
    machinerySuperstSpecification: string; // Required if machineryInsuredObject = 'N'
    machineryDeductible: number; // 5, 10, 15, 20, or 30 representing percentage
  };
  assistance: {
    rate: string; // VARPOJ - 4110, 4120, 4130, 4140, 4150, 4160, 4173
    extensionToTowedVehicle: boolean; // ASIST_PRIPV
  };
  accident: {
    whoCovered: string; // "driver" or "allSeats"
    injuryPlan: string; // "UM", "US", or "UV"
    excludeSleepingPlaces: boolean;
    includeUnfaultedProtection: boolean;
  };
  windshield: {
    type: string; // "front" or "all"
    insuranceLimit: number; // HRIZ - 4,000 to 500,000 CZK
    deductible: number;
    repairPriority: boolean;
    originalParts: boolean;
  };
  gap: {
    gapType: string; // "gap" or "gapWithDeductible"
    vehiclePurchasePrice: number; // Vehicle purchase price in CZK
    includeFinancialLoss: boolean; // GAP-U Financial Loss Insurance
  };
  cargo: {
    cargoType: string; // "notSelected", "withoutTheft", "withTheft"
    cargoLimit: number; // 20,000 to 1,000,000 CZK
    cargoRiskGroup: string; // "1", "2", "3"
    luggageType: string; // "notSelected", "withoutTheft", "withTheft"
    luggageLimit: number; // 5,000 to 500,000 CZK
  };
  legalProtection: {
    sumInsured: number;
    scope: string;
    includeFamily: boolean;
  };
  damages: {
    naturalHazard: boolean;
    naturalHazardLimit: number; // 50,000 to 1,000,000 CZK
    collisionWithAnimal: boolean;
    potholeDamage: boolean;
    replacementVehicle: boolean;
    replacementVehicleDailyLimit: number; // 900, 1500, 2000, 3500, 5000
    replacementVehicleDays: number; // 5, 10, 15, 20
  };
}

// Add premium information to each coverage
export interface CoveragePremium {
  mtpl?: number;
  allRisk?: number;
  assistance?: number;
  accident?: number;
  windshield?: number;
  gap?: number;
  cargo?: number;
  legalProtection?: number;
  damages?: number;
}

// Shared props interface for coverage components
export interface CoverageComponentProps<T extends keyof CoverageConfig> {
  config: CoverageConfig[T];
  onConfigChange: (updates: Partial<CoverageConfig[T]>) => void;
  premium?: number;
  onPremiumChange?: (premium: number) => void;
  t: any;
  classes: any;
  form?: any; // Optional form prop for validation
}

// Coverage definition interface
export interface CoverageDefinition {
  type: CoverageType;
  icon: string;
  required: boolean;
}
